/* eslint-disable @typescript-eslint/no-explicit-any */
import has from 'lodash/has';

export type GenericConfig = {[key: string]: unknown};
export type Configurator = <T = any>(_name: string, _dflt?: T) => T;

const noDefaultSymbol = Symbol('noDefaultSymbol');

export const makeWriteConfigurator = (config: GenericConfig): Configurator => (
  name: string,
  dflt: unknown = noDefaultSymbol,
): any => {
  const noPrev = !has(config, name);
  const noDflt = dflt === noDefaultSymbol;
  if (noDflt && noPrev) {
    throw Error(`Declaration for parameter "${name}" is required`);
  } else if (noPrev) {
    config[name] = dflt;
    return dflt;
  } else if (noDflt) {
    return config[name];
  } else if (config[name] === dflt) {
    return dflt;
  } else {
    throw new Error(`Double definition of config "${name}": ${config[name]} !== ${dflt}`);
  }
};

export const makeReadConfigurator = (config: GenericConfig): Configurator => {
  const defaults = {};
  const defaultsConfigurator = makeWriteConfigurator(defaults);
  const configurator = (name: string, dflt: unknown = noDefaultSymbol): any => {
    const defaultsRes = defaultsConfigurator(name, dflt);
    if (!has(config, name)) {
      return defaultsRes;
    }
    return config[name];
  };
  configurator.defaults = defaults;
  return configurator;
};

export const makeStrictReadConfigurator = (config: GenericConfig): Configurator => (
  name: string,
  ..._: unknown[]
): any => {
  if (!has(config, name)) {
    throw new Error(`No config entry for "${name}"`);
  }
  return config[name];
};

export const makeTypedReadConfigurator = <C>(config: C) => <K extends keyof C>(
  name: K,
  ..._: unknown[]
): C[K] => {
  if (!has(config, name)) {
    throw new Error(`No config entry for "${name}"`);
  }
  return config[name];
};
